/**
 * Mandar um edital que alguém achou fora do sistema, e receber a análise.
 *
 * É a terceira situação que a Nectar pediu, e a única entrada do funil que não
 * começa numa fonte: aqui QUEM É O PORTÃO É A PESSOA. Ela leu, achou
 * relevante, e mandou — então o vocabulário e os filtros de prazo não se
 * aplicam, e o edital não entra nas contas do acervo, que medem alcance de
 * coleta.
 *
 * ── três coisas que a tela precisa dizer, e por quê ──────────────────────
 *
 * **Que está trabalhando.** A análise é síncrona e leva dezenas de segundos:
 * lê o PDF, chama o modelo, cruza com os 201 atestados e pontua. Sem sinal
 * disso, quem manda acha que travou e manda de novo.
 *
 * **Que já existia, quando já existia.** Mandar o edital que outra pessoa já
 * mandou é o caso comum. Dizer "análise pronta" nas duas situações esconderia
 * que a segunda não custou nada e não recalculou nada — e faria parecer que o
 * sistema é mais rápido do que é.
 *
 * **O que deu errado, quando dá.** PDF digitalizado sem camada de texto é o
 * caso que mais aparece, e a mensagem do servidor já diz o que fazer. Engolir
 * isso num "erro ao enviar" transformaria um problema resolvível em mistério.
 *
 * AS PEÇAS DO DESIGN SYSTEM (20/10): o cabeçalho, o botão de escolher o
 * arquivo, os avisos (o erro e o "já existia"), a seção dos enviados com a
 * contagem, o vazio e o ícone do documento saem de `src/ds`. Ficam em
 * `Editais.module.css` o que é desta tela: a área de arrastar, os passos do
 * "como funciona" e a linha de cada edital enviado (com a nota do score).
 */
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ErroApi } from "../api/cliente";
import { Nota } from "../componentes/Selos";
import { Aviso, Botao, Cabecalho, IconeDocumento, Secao, Vazio } from "../ds";
import css from "./Editais.module.css";

const ACEITOS = ".pdf,.txt";

function quando(iso: string) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString("pt-BR");
}

export function Editais() {
  const cliente = useQueryClient();
  const navegar = useNavigate();
  const campo = useRef<HTMLInputElement>(null);
  const [sobre, setSobre] = useState(false);

  const lista = useQuery({ queryKey: ["editais"], queryFn: api.editais });

  const envio = useMutation({
    mutationFn: (a: File) => api.enviarEdital(a),
    onSuccess: (r) => {
      cliente.invalidateQueries({ queryKey: ["editais"] });
      cliente.invalidateQueries({ queryKey: ["licitacoes"] });
      // Vai direto para o detalhe: a análise inteira mora lá — os cinco
      // critérios do score, os atestados que o sustentam, e os botões de
      // decisão. Repetir isso aqui seria uma segunda cópia da mesma tela.
      //
      // COM QUALQUER SCORE (decisão do Pedro, 2026-10-05): "na tela de enviar
      // edital, o score e a pré-análise devem ser gerados independentemente
      // da regra do score limiar de 50". Até ali o score abaixo de 50 fechava
      // o detalhe do próprio edital de quem o mandou (Regra 1, 404), e esta
      // tela parava aqui para avisar. Agora o servidor abre o detalhe do
      // edital enviado sempre, e é lá que o score e a pré-análise aparecem.
      navegar(`/licitacao/${r.licitacao_id}`);
    },
  });

  function manda(arquivos: FileList | null) {
    const a = arquivos?.[0];
    if (a) envio.mutate(a);
  }

  const erro =
    envio.error instanceof ErroApi
      ? envio.error.message
      : envio.error
        ? String(envio.error)
        : null;

  return (
    <div className={css.tela}>
      <Cabecalho
        titulo="Enviar edital"
        descricao={
          <>
            Achou um edital fora do sistema? Envie o arquivo e ele passa pelo
            mesmo caminho das oportunidades coletadas: leitura, cruzamento com os
            atestados do Nectar, score e pré-análise.
          </>
        }
      />

      {/* O ENVIO E O COMO FUNCIONA, LADO A LADO (20/10, "mais explicativa,
          clean e organizada"): à esquerda a ação, à direita o que acontece
          depois dela — os mesmos passos que o servidor faz, nesta ordem. No
          celular o como funciona desce para baixo da área. */}
      <div className={css.envio}>
        <div
          className={`${css.area} ${sobre ? css.sobre : ""} ${envio.isPending ? css.ocupada : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setSobre(true);
          }}
          onDragLeave={() => setSobre(false)}
          onDrop={(e) => {
            e.preventDefault();
            setSobre(false);
            if (!envio.isPending) manda(e.dataTransfer.files);
          }}
        >
          <input
            ref={campo}
            type="file"
            accept={ACEITOS}
            hidden
            onChange={(e) => {
              manda(e.target.files);
              // Zera para que mandar o MESMO arquivo de novo dispare o onChange.
              // Sem isto, quem corrigir um erro e reenviar o mesmo nome não vê
              // nada acontecer.
              e.target.value = "";
            }}
          />
          {envio.isPending ? (
            <>
              <span className={css.girando} aria-hidden="true" />
              <strong>Analisando o edital…</strong>
              <span className={css.dica} role="status">
                Lendo o documento, conferindo o escopo, cruzando com os atestados
                e calculando o score. Leva alguns instantes — não feche esta
                página.
              </span>
            </>
          ) : (
            <>
              <svg className={css.icone} viewBox="0 0 32 32" fill="none" stroke="currentColor"
                   strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 4h10l6 6v16a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Z" />
                <path d="M19 4v6h6M16 23v-8M12.5 18.5 16 15l3.5 3.5" />
              </svg>
              <strong className={css.soArraste}>Arraste o edital para cá</strong>
              <strong className={css.soToque}>Escolha o edital no aparelho</strong>
              <span className={css.dica}>
                <span className={css.soArraste}>ou </span>escolha o arquivo · PDF ou
                texto (.txt), até 25 MB
              </span>
              <Botao variante="primario" className={css.botao} onClick={() => campo.current?.click()}>
                Escolher arquivo
              </Botao>
            </>
          )}
        </div>

        <aside className={css.comoFunciona} aria-labelledby="como-funciona">
          <h2 id="como-funciona">Como funciona</h2>
          <ol className={css.passos}>
            <li>
              <b>Envie o arquivo</b>
              <span>Um edital por vez, em PDF ou texto, até 25 MB.</span>
            </li>
            <li>
              <b>Leitura e escopo</b>
              <span>A IA lê o edital e confere se o objeto é do escopo do Nectar.</span>
            </li>
            <li>
              <b>Cruzamento e score</b>
              <span>
                O objeto é comparado com os atestados do Nectar e recebe o score —
                mesmo abaixo de 50, que esconderia uma oportunidade coletada.
              </span>
            </li>
            <li>
              <b>A oportunidade abre</b>
              <span>
                Você vai direto para ela. A ficha técnica e a pré-análise do
                edital chegam em seguida, em segundo plano.
              </span>
            </li>
          </ol>
          <p className={css.nota}>
            Edital idêntico a um já enviado não é analisado de novo: abre a
            análise que já existe, sem custo.
          </p>
        </aside>
      </div>

      {erro && <Aviso tom="erro" titulo="Não deu para analisar.">{erro}</Aviso>}

      {envio.data?.ja_existia && (
        <Aviso>
          Este edital já tinha sido analisado — o texto é idêntico a um que já
          está aqui. Nada foi recalculado.
        </Aviso>
      )}

      <Secao titulo="Editais enviados" conta={lista.data?.length || undefined}>
        {lista.isPending ? (
          <Vazio>Carregando…</Vazio>
        ) : !lista.data?.length ? (
          <Vazio>
            Nenhum edital enviado ainda. Os que chegarem por aqui aparecem nesta
            lista e nas abas de oportunidades, e não entram nas contas da coleta.
          </Vazio>
        ) : (
          <ul className={css.itens}>
            {lista.data.map((e) => (
              <li key={e.licitacao_id}>
                <button
                  type="button"
                  onClick={() => navegar(`/licitacao/${e.licitacao_id}`)}
                >
                  {/* O ENVIADO QUE AGUARDA A LEITURA (D17, 2026-10-05) vem do
                      servidor sem score: a nota diz por quê em vez de "ainda não
                      pontuado", e a linha de baixo escreve o mesmo, para o toque. */}
                  <Nota
                    valor={e.score}
                    recomendacao={e.recomendacao}
                    faixa={e.aguardando_leitura ? "aguardando" : undefined}
                  />
                  <span className={css.corpo}>
                    <span className={css.objeto}>{e.objeto}</span>
                    <span className={css.meta}>
                      <span className={css.arquivo}>
                        <IconeDocumento tamanho={13} />
                        {e.nome_arquivo}
                      </span>
                      <span>
                        {e.paginas ? `${e.paginas} páginas · ` : ""}
                        {e.caracteres.toLocaleString("pt-BR")} caracteres
                      </span>
                      <span>
                        {e.enviado_por} · {quando(e.enviado_em)}
                      </span>
                      {e.aguardando_leitura ? <span>aguardando leitura do edital</span> : null}
                    </span>
                  </span>
                  <svg className={css.seta} viewBox="0 0 16 16" width="16" height="16" fill="none"
                       stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
                       strokeLinejoin="round" aria-hidden="true">
                    <path d="m6 3.5 4.5 4.5L6 12.5" />
                  </svg>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Secao>
    </div>
  );
}
